import {
  ErpFieldMappingTable,
  buildErpRowsFromSourceAnswers,
} from '@/components/Customers/ErpFieldMappingTable'
import { getQuestionTypeZhName } from '@/components/FormBuilder/question-type-meta'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { ScrollArea } from '@/components/ui/scroll-area'
import type {
  CustomerApplicationFieldSourceState,
  QuestionnaireSourceAnswerDetail,
  QuestionnaireSourceRecordContract,
} from '@/contracts/customer-application-binding'
import { useResizablePane } from '@/hooks/use-resizable-pane'
import { getTwoPaneGridTemplate, sourceRecordPaneDefaults } from '@/lib/resizable-panes'
import { cn } from '@/lib/utils'
import type { QuestionType } from '@/types/form'
import { FileText } from 'lucide-react'
import { type CSSProperties, useEffect, useState } from 'react'

const sourceStateLabel: Record<CustomerApplicationFieldSourceState, string> = {
  prefilled_from_question: '已對應欄位',
  manual: '未綁定欄位',
  overridden: '已覆寫',
  missing_source: '缺少來源',
}

export function QuestionnaireSourceRecordDetailDialog({
  record,
  open,
  onOpenChange,
}: {
  record: QuestionnaireSourceRecordContract | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null)
  const {
    width: navWidth,
    collapsed: navCollapsed,
    startResize: startNavResize,
    toggleCollapsed: toggleNavCollapsed,
  } = useResizablePane({
    initialWidth: 280,
    config: sourceRecordPaneDefaults.nav,
  })
  const sourceRecordGridColumns = getTwoPaneGridTemplate({
    navWidth,
    navCollapsed,
    contentMinWidth: 0,
    navConfig: sourceRecordPaneDefaults.nav,
  })

  useEffect(() => {
    setActiveQuestionId(record?.answerDetails[0]?.questionId ?? null)
  }, [record])

  const activeAnswer =
    record?.answerDetails.find((answer) => answer.questionId === activeQuestionId) ??
    record?.answerDetails[0] ??
    null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(760px,calc(100dvh-48px))] max-h-none max-w-5xl flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-5 py-4 pr-12">
          <div className="flex flex-wrap items-center gap-2">
            <DialogTitle className="text-base">問卷詳細資訊</DialogTitle>
            {record ? <Badge variant="outline">{record.formTitle}</Badge> : null}
          </div>
          <DialogDescription>
            查看這筆問卷記錄的問題清單、回答內容與欄位 binding 狀態。
          </DialogDescription>
        </DialogHeader>
        {record ? (
          <div
            data-testid="source-record-detail-grid"
            className="grid min-h-0 flex-1 transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--source-record-grid-columns)]"
            style={{ '--source-record-grid-columns': sourceRecordGridColumns } as CSSProperties}
          >
            <div className="relative min-h-0 min-w-0 overflow-hidden">
              <aside
                className={cn(
                  'min-h-0 border-b border-slate-200 bg-slate-50 transition-opacity duration-150 lg:h-full lg:border-r lg:border-b-0',
                  navCollapsed && 'pointer-events-none opacity-0',
                )}
                aria-hidden={navCollapsed}
              >
                <div className="border-b border-slate-200 bg-white px-4 py-3">
                  <div className="text-sm font-semibold text-slate-950">問題清單</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {record.submittedByName ?? '未命名提交者'} · {record.submittedAt}
                  </div>
                </div>
                <ScrollArea className="h-[calc(100%-65px)]" viewportClassName="p-3">
                  <div className="space-y-2">
                    {record.answerDetails.map((answer, index) => {
                      const active = answer.questionId === activeAnswer?.questionId
                      return (
                        <button
                          key={`${answer.questionId}-${index}`}
                          type="button"
                          aria-current={active ? 'true' : undefined}
                          aria-label={`查看第 ${index + 1} 題回答：${answer.questionTitle}`}
                          className={cn(
                            'flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                            active
                              ? 'border-indigo-200 bg-indigo-50 text-indigo-800'
                              : 'border-transparent bg-white text-slate-600 hover:border-slate-200',
                          )}
                          onClick={() => setActiveQuestionId(answer.questionId)}
                        >
                          <FileText className="mt-0.5 size-4 shrink-0 text-slate-400" />
                          <span className="min-w-0 flex-1">
                            <span className="line-clamp-2 font-medium">{answer.questionTitle}</span>
                            {answer.mappedTargetLabel ? (
                              <span className="mt-1 block text-xs text-slate-500">
                                對應 {answer.mappedTargetLabel}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </ScrollArea>
              </aside>
              <PaneResizeHandle
                edge="right"
                collapsed={navCollapsed}
                resizeLabel="拖曳調整問卷記錄左欄寬度"
                collapseLabel="收合問卷記錄左欄"
                expandLabel="展開問卷記錄左欄"
                onResizeStart={startNavResize}
                onToggle={toggleNavCollapsed}
              />
            </div>
            <ScrollArea className="min-h-0" viewportClassName="p-6">
              {activeAnswer ? <AnswerDetail answer={activeAnswer} /> : null}
            </ScrollArea>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function AnswerDetail({ answer }: { answer: QuestionnaireSourceAnswerDetail }) {
  const erpRows = buildErpRowsFromSourceAnswers([answer])

  return (
    <div className="space-y-5">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold text-slate-950">{answer.questionTitle}</h3>
          <Badge variant="secondary">
            {getQuestionTypeZhName(answer.questionType as QuestionType)}
          </Badge>
          <Badge variant={answer.mappedTarget ? 'success' : 'outline'}>
            {sourceStateLabel[answer.sourceState]}
          </Badge>
        </div>
        {answer.mappedTargetLabel ? (
          <p className="mt-2 text-sm text-slate-500">
            這題回答會帶入申請表欄位：{answer.mappedTargetLabel}
          </p>
        ) : (
          <p className="mt-2 text-sm text-slate-500">
            這題沒有設定欄位 binding，仍會保留在問卷記錄詳情中。
          </p>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-xs font-medium text-slate-500">回答內容</div>
        <div className="mt-3 whitespace-pre-wrap text-base leading-7 text-slate-900">
          {answer.valuePreview || '-'}
        </div>
      </div>
      <div className="grid gap-3 text-sm md:grid-cols-2">
        <Info label="Question ID" value={answer.questionId} />
        <Info label="對應欄位" value={answer.mappedTargetLabel ?? '未設定'} />
      </div>
      <ErpFieldMappingTable
        rows={erpRows}
        title="這題對應的 ERP 客戶欄位"
        description="顯示目前問卷答案會對應到哪個 ERP 客戶欄位、DB 欄位與資料型別。"
      />
    </div>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 break-all font-medium text-slate-800">{value}</div>
    </div>
  )
}
